/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { DateRangeOption } from '../../utils/dateRangeUtils.ts';
import LoadingSpinner from '../LoadingSpinner.tsx';

interface CollectionLoadingStatesProps {
  // State flags
  isAdmin: boolean;
  hasUserMadeSelection: boolean;
  isLoadingCollectionData: boolean;
  
  // Data
  tagsArray: any;
  apiError: string | null;
  dateRange: DateRangeOption;
  availableOptions: Array<{ value: DateRangeOption; label: string }>;
  cacheSize: number;
  selectedCollectionName?: string;
  
  // Callbacks
  onDateRangeChange: (range: DateRangeOption) => void;
  onRetry: () => void;
}

const CollectionLoadingStates: React.FC<CollectionLoadingStatesProps> = ({
  isAdmin,
  hasUserMadeSelection,
  isLoadingCollectionData,
  tagsArray,
  apiError,
  dateRange,
  availableOptions,
  cacheSize,
  selectedCollectionName,
  onDateRangeChange,
  onRetry
}) => {
  
  // Check if we should show admin "no selection" state
  if (isAdmin && !hasUserMadeSelection && tagsArray && tagsArray.length > 0) {
    return (
      <div className="container shopify-sales-grid-container">
        <div className="admin-mode-indicator" style={{ 
          background: '#d4edda', 
          color: '#155724', 
          padding: '8px', 
          borderRadius: '4px', 
          marginBottom: '10px',
          fontSize: '12px'
        }}>
          <strong>Admin Mode:</strong> Data will be loaded when you select a collection
          {cacheSize > 0 && (
            <span style={{ marginLeft: '10px' }}>
              ({cacheSize} collections cached)
            </span>
          )}
        </div>

        <div className="selection-prompt" style={{ 
          padding: '40px 20px', 
          textAlign: 'center', 
          background: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          <h3>Select a collection to view sales data</h3>
          <p>Choose a collection from the dropdown in the header above to load and display its sales information.</p>
        </div>

        {/* Date Range Selector */}
        <div style={{
          padding: '12px',
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
            Date Range:
          </label>
          <select
            value={dateRange}
            onChange={(e) => onDateRangeChange(e.target.value as DateRangeOption)}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            {availableOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {dateRange === 'all_time' && (
            <span style={{ fontSize: '12px', color: '#856404', background: '#fff3cd', padding: '4px 8px', borderRadius: '4px' }}>
              ⏳ Loading all historical data may take longer
            </span>
          )}
        </div>
      </div>
    );
  }

  // Check if we should show debug info state
  if (!tagsArray || !Array.isArray(tagsArray) || tagsArray.length === 0) {
    return (
      <div className="debug-info" style={{ padding: '20px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
        <h3>Debug Information:</h3>
        <p><strong>isAdmin:</strong> {isAdmin ? 'Yes' : 'No'}</p>
        <p><strong>hasUserMadeSelection:</strong> {hasUserMadeSelection ? 'Yes' : 'No'}</p>
        <p><strong>tagsArray type:</strong> {typeof tagsArray}</p>
        <p><strong>tagsArray is array:</strong> {Array.isArray(tagsArray) ? 'Yes' : 'No'}</p>
        <p><strong>tagsArray length:</strong> {tagsArray?.length || 'N/A'}</p>
        {apiError && <p style={{ color: 'red' }}><strong>API Error:</strong> {apiError}</p>}
        <hr />
        <p>No data available</p>
      </div>
    );
  }

  // Check if we should show admin loading state
  if (isAdmin && isLoadingCollectionData) {
    return (
      <div className="loading-container" style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Loading collection data...</h3>
        <p>Fetching data for: {selectedCollectionName}</p>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
          Large collections may take up to 2 minutes to load.
        </p>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  // Check if we should show error state
  if (isAdmin && apiError) {
    return (
      <div className="error-container" style={{ 
        padding: '20px', 
        background: '#f8d7da', 
        border: '1px solid #f5c6cb', 
        borderRadius: '4px' 
      }}>
        <h3>Error loading collection data</h3>
        <p style={{ marginBottom: '15px' }}>{apiError}</p>
        
        {apiError.includes('timed out') && (
          <div style={{
            padding: '10px',
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            marginBottom: '15px',
            fontSize: '14px'
          }}>
            <strong>Large Collection Detected:</strong>
            <p style={{ marginTop: '5px', marginBottom: 0 }}>
              This collection appears to have a large number of orders. The request may have exceeded the timeout limit.
              Try refreshing the page or contact support if this issue persists.
            </p>
          </div>
        )}
        
        <button 
          onClick={onRetry}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // None of the special states apply - return null to indicate normal rendering
  return null;
};

export default CollectionLoadingStates;